import { useUser } from '@clerk/clerk-react';
import {
    useCallback,
    useMemo,
    useRef,
    useState,
    type ReactElement,
    type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { usePlan } from '@/billing/plan-context';
import { UpgradeContext, type Upgrade, type UpgradeReason } from '@/billing/use-upgrade';
import { LimitDialog, useActiveDiagrams as useStoreActiveDiagrams, type ActiveDiagram } from '@/components/billing/limit-dialog';
import { UpgradeDialog } from '@/components/billing/upgrade-dialog';
import { getDiagramStore } from '@/diagram-persistence/store-instance';
import { SyncedDiagramStore } from '@/diagram-persistence/synced-diagram-store';
import { buildCheckoutUrl } from '@/lib/checkout-url';

export { useUpgrade } from '@/billing/use-upgrade';
export type { Upgrade, UpgradeReason };

/** Archives on the server; the device copy is not touched. Only a signed-in (synced) store can.
 *  The store tells every gallery to re-list, so the archived card leaves the active lists. */
async function archiveInStore(id: string): Promise<void> {
    const store = getDiagramStore();
    if (!(store instanceof SyncedDiagramStore)) throw new Error('Archiving needs a signed-in account');
    await store.archive(id);
}

/**
 * Owns the limit dialog and the upgrade dialog and hands the rest of the app
 * three calls to reach them (`useUpgrade`). Mount it inside `PlanProvider`, which
 * is where the limit and the checkout URL come from.
 *
 * `archive` and `useActiveDiagrams` are the seams to the diagram store; the
 * defaults are the real ones.
 */
export function UpgradeProvider({
    children,
    archive = archiveInStore,
    useActiveDiagrams = useStoreActiveDiagrams,
}: {
    children: ReactNode;
    archive?: (id: string) => Promise<void>;
    useActiveDiagrams?: () => ActiveDiagram[] | null;
}): ReactElement {
    const { t } = useTranslation();
    const plan = usePlan();
    const { user } = useUser();
    const limit = plan.activeDiagramLimit;
    const { refresh } = plan;

    const [open, setOpen] = useState<'limit' | 'upgrade' | null>(null);
    // Kept after the upgrade dialog closes so its text doesn't blank out during the exit animation.
    const [reason, setReason] = useState<UpgradeReason | null>(null);
    // What to do once an archive frees a slot. A ref: it's a function the dialog never renders.
    const pendingRef = useRef<(() => void) | null>(null);

    // The URL is built per signed-in user (the webhook needs their id). Null → nothing to buy.
    const checkoutHref = useMemo(
        () => (plan.checkoutUrl != null && user?.id ? buildCheckoutUrl(plan.checkoutUrl, user.id) : null),
        [plan.checkoutUrl, user?.id],
    );

    const upgrade = useMemo<Upgrade>(
        () => ({
            withSlot: (activeCount, action) => {
                // No limit (Pro, not enforced, or not known yet) never blocks anyone.
                if (limit === null || activeCount < limit) {
                    action();
                    return;
                }
                pendingRef.current = action;
                setOpen('limit');
            },
            showLimit: (then) => {
                // A server refusal can beat the plan fetch, or outlive a cached limit: ask again so the body has a number.
                if (limit === null) void refresh();
                if (then != null) pendingRef.current = then;
                setOpen('limit');
            },
            showUpgrade: (next) => {
                // Replacing the limit dialog: whatever it was holding is no longer waiting on an archive.
                pendingRef.current = null;
                setReason(next);
                setOpen('upgrade');
            },
        }),
        [limit, refresh],
    );

    const dismiss = useCallback(() => {
        // The action the user was after is dropped, not deferred: they chose not to make room.
        pendingRef.current = null;
        setOpen(null);
    }, []);

    const archiveAndContinue = useCallback(
        async (id: string): Promise<boolean> => {
            try {
                await archive(id);
            } catch {
                toast.error(t('billing.limit.archiveFailed'));
                return false;
            }
            const run = pendingRef.current;
            pendingRef.current = null;
            setOpen(null);
            run?.();
            return true;
        },
        [archive, t],
    );

    return (
        <UpgradeContext.Provider value={upgrade}>
            {children}
            <LimitDialog
                open={open === 'limit'}
                limit={limit}
                checkoutHref={checkoutHref}
                useDiagrams={useActiveDiagrams}
                onArchive={archiveAndContinue}
                onDismiss={dismiss}
            />
            {reason != null && (
                <UpgradeDialog open={open === 'upgrade'} reason={reason} checkoutHref={checkoutHref} onDismiss={dismiss} />
            )}
        </UpgradeContext.Provider>
    );
}
